import { cloneSpec, FLEET, hydrateSceneryAirport, lookupAirport, sceneryFromRecord } from "./airports";
import { aircraftFromSetupTail, emptySetupTail, icaoTypeDesignatorError, type SetupTailDraft } from "./setupFleet";
import { newCrewMember } from "./crew";
import { resolveAirportRunway } from "./airportRunways";
import {
  completeCompanySetup,
  hydrateCompany,
  normalizeIcaoCode,
  type CompanySetupInput,
} from "./company";
import { catalogsAsStubs, catalogsFromUnknown, hydrateCurrency, hydrateMoneyCatalogs, icaosFromScenery, type CurrencyCode } from "./catalogs";
import { defaultAccount, emptyAccount } from "./ledger";
import { packLogoDataUrl, sanitizeAirlineLogoPlate } from "./airlineBrand";
import { SUGGESTED_COMPANY_NAME, SUGGESTED_HOME_ICAO } from "./profile";
import { regionalParty } from "./regionalParty";
import type {
  AircraftSpec,
  CareerState,
  CharterRequest,
  CompanyAccount,
  CompanyKind,
  CompanyProfile,
  CrewMember,
  PlayMode,
  SceneryAirport,
  SceneryPresence,
} from "./types";
import {
  effectiveBeginningScenarioText,
  openingJobPair,
  type CareerSeed,
} from "./careerScenario";

export const MIN_CAREER_FLEET = 1;
export const MAX_CAREER_FLEET = 10;

export const COMPANY_KIND_LABEL: Record<CompanyKind, string> = {
  charter: "On-demand charter",
  air_taxi: "Air taxi",
  corporate: "Corporate flight department",
  owner: "Owner-operator",
};

export const DEFAULT_CAREER: CareerState = {
  playMode: "unset",
  scenarioConfirmed: false,
  destinationsInstalledOnly: false,
  catalogStubs: catalogsAsStubs(hydrateMoneyCatalogs()),
  beginningNotes: "",
  airlineName: "",
  airlineLogo: "",
  airlineLogoKeptLocal: false,
  airlineLogoPlate: "",
};

const AIRLINE_NAME_MAX = 80;
const AIRLINE_LOGO_MAX = 1_500_000;
const AIRLINE_DATA_URL = /^data:image\/(png|jpeg|jpg|webp|gif|svg\+xml);base64,[a-z0-9+/=\s]+$/i;
const AIRLINE_ASSET_REF = /^(?:asset:|brand:)[a-z0-9][a-z0-9._/-]{0,120}$/i;

/** Strip control characters. Empty when the value is not a short display name. */
export function sanitizeAirlineName(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return raw.replace(/[\u0000-\u001f]/g, "").trim().slice(0, AIRLINE_NAME_MAX);
}

/**
 * What the airline-name box shows while someone is typing.
 * The stored name stays trimmed. The draft keeps a trailing space, so a second word can be entered.
 */
export function airlineNameWhileTyping(typed: string): { draft: string; stored: string } {
  const draft = typed.replace(/[\u0000-\u001f]/g, "").slice(0, AIRLINE_NAME_MAX);
  return { draft, stored: sanitizeAirlineName(draft) };
}

/**
 * Keep a data-image URL or a later logo-pack ref. Anything else (web URL,
 * script URL, empty) is dropped so the desk never points an image at it.
 */
export function sanitizeAirlineLogo(raw: unknown): string {
  if (typeof raw !== "string") return "";
  const value = raw.trim();
  if (!value || value.length > AIRLINE_LOGO_MAX) return "";
  if (AIRLINE_DATA_URL.test(value)) return value.replace(/\s+/g, "");
  if (AIRLINE_ASSET_REF.test(value)) return value;
  return "";
}

/** Career name when the virtual airline name has not been set. */
export function airlineDisplayName(
  career: { airlineName?: string } | null | undefined,
  careerName: string,
): string {
  return sanitizeAirlineName(career?.airlineName) || careerName.trim();
}

/**
 * A src that is safe to put on an img. Data URLs pass through. An asset ref
 * renders only when `assets` maps it to a data URL. Otherwise null — no image.
 */
export function airlineLogoSrc(
  raw: unknown,
  assets?: Readonly<Record<string, string>>,
  opts?: { onDark?: boolean },
): string | null {
  const clean = sanitizeAirlineLogo(raw);
  if (!clean) return null;
  if (clean.startsWith("data:image/")) return clean;
  if (clean.startsWith("brand:")) {
    const packed = packLogoDataUrl(clean, opts?.onDark === true);
    return packed && sanitizeAirlineLogo(packed) ? packed : null;
  }
  const mapped = assets?.[clean];
  if (!mapped) return null;
  const resolved = sanitizeAirlineLogo(mapped);
  return resolved && resolved.startsWith("data:image/") ? resolved : null;
}

export const SUGGESTED_PRIMARY_REG = "C-GVXL";

export type CareerFleetRow = {
  icaoType: string;
  registration: string;
  name?: string;
  id?: string;
  oewLb?: number;
  mzfwLb?: number;
  mlwLb?: number;
  mtowLb?: number;
  maxFuelLb?: number;
  fuelBurnGph?: number;
  rangeNm?: number;
  ferryRangeNm?: number;
  cruiseKt?: number;
  longRangeCruiseKt?: number;
  averageCruiseKt?: number;
  cruiseMach?: number;
  climbRateFpm?: number;
  descentRateFpm?: number;
  engineHours?: number;
  remarks?: string;
  engines?: string;
  cabin?: string;
  year?: string;
  role?: string;
  maxPax?: number;
  ceilingFt?: number;
  takeoffDistanceFt?: number;
  landingDistanceFt?: number;
};

export type CareerSetupUser = {
  displayName: string;
  roles: Array<"dispatcher" | "pilot">;
  isAdmin: boolean;
  ratedIcaoTypes: string[];
  pinSalt: string;
  pinHash: string;
};

export function hydrateCareer(
  raw?: Partial<CareerState> | null,
  opts: { companyConfigured?: boolean } = {},
): CareerState {
  const playMode = raw?.playMode;
  const inferred: PlayMode =
    playMode === "career" || playMode === "casual"
      ? playMode
      : opts.companyConfigured
        ? "career"
        : "unset";
  return {
    playMode: inferred,
    scenarioConfirmed: Boolean(raw?.scenarioConfirmed) || inferred === "casual" || Boolean(opts.companyConfigured && inferred === "career"),
    destinationsInstalledOnly: Boolean(raw?.destinationsInstalledOnly),
    catalogStubs: catalogsAsStubs(
      hydrateMoneyCatalogs(catalogsFromUnknown(raw?.catalogStubs), {
        icaos: Array.isArray((raw as { destinationIcaos?: string[] })?.destinationIcaos)
          ? (raw as { destinationIcaos?: string[] }).destinationIcaos
          : undefined,
      }),
    ),
    beginningNotes: typeof raw?.beginningNotes === "string" ? raw.beginningNotes : "",
    test: raw?.test === true ? true : undefined,
    testId: raw?.test === true && typeof raw.testId === "string" ? raw.testId : undefined,
    airlineName: sanitizeAirlineName(raw?.airlineName),
    airlineLogo: sanitizeAirlineLogo(raw?.airlineLogo),
    airlineLogoKeptLocal: raw?.airlineLogoKeptLocal === true,
    airlineLogoPlate: sanitizeAirlineLogoPlate(raw?.airlineLogoPlate),
  };
}

export function playModeChosen(career: CareerState | undefined): boolean {
  return career?.playMode === "career" || career?.playMode === "casual";
}

export function suggestedCareerFleet(): CareerFleetRow[] {
  return [
    { icaoType: "H25B", registration: SUGGESTED_PRIMARY_REG, name: "Hawker 800XP" },
    { icaoType: "PC12", registration: "C-GNSP", name: "Pilatus PC-12 NG" },
  ];
}

export function normalizeFleetRegistration(raw: string): string {
  return raw.trim().toUpperCase().replace(/\s+/g, "");
}

export function validateCareerFleet(rows: CareerFleetRow[]): { ok: true; rows: CareerFleetRow[] } | { ok: false; error: string } {
  const cleaned = rows
    .map((row) => ({
      ...row,
      icaoType: row.icaoType.trim().toUpperCase(),
      registration: normalizeFleetRegistration(row.registration),
      name: row.name?.trim(),
    }))
    .filter((row) => row.icaoType && row.registration);
  if (cleaned.length < MIN_CAREER_FLEET) {
    return { ok: false, error: `Charter fleet must be at least ${MIN_CAREER_FLEET} aircraft.` };
  }
  if (cleaned.length > MAX_CAREER_FLEET) {
    return { ok: false, error: `Charter fleet is capped at ${MAX_CAREER_FLEET} aircraft.` };
  }
  const regs = new Set<string>();
  for (const row of cleaned) {
    if (regs.has(row.registration)) {
      return { ok: false, error: `Duplicate registration ${row.registration}.` };
    }
    regs.add(row.registration);
    const typeErr = icaoTypeDesignatorError(row.icaoType);
    if (typeErr) return { ok: false, error: typeErr };
  }
  return { ok: true, rows: cleaned };
}

function rowHasFigures(row: CareerFleetRow): boolean {
  return [row.mtowLb, row.mlwLb, row.mzfwLb, row.oewLb, row.maxFuelLb, row.fuelBurnGph, row.rangeNm, row.cruiseKt].some(
    (n) => typeof n === "number" && n > 0,
  );
}

function draftFromRow(row: CareerFleetRow): SetupTailDraft {
  const blank = emptySetupTail();
  return {
    ...blank,
    id: row.id,
    icaoType: row.icaoType,
    registration: row.registration,
    name: row.name ?? "",
    oewLb: row.oewLb ?? 0,
    mzfwLb: row.mzfwLb ?? 0,
    mlwLb: row.mlwLb ?? 0,
    mtowLb: row.mtowLb ?? 0,
    maxFuelLb: row.maxFuelLb ?? 0,
    fuelBurnGph: row.fuelBurnGph ?? 0,
    rangeNm: row.rangeNm ?? 0,
    ferryRangeNm: row.ferryRangeNm ?? 0,
    cruiseKt: row.cruiseKt ?? 0,
    longRangeCruiseKt: row.longRangeCruiseKt ?? 0,
    averageCruiseKt: row.averageCruiseKt ?? 0,
    cruiseMach: row.cruiseMach ?? 0,
    climbRateFpm: row.climbRateFpm ?? 0,
    descentRateFpm: row.descentRateFpm ?? 0,
    engineHours: row.engineHours ?? 0,
    remarks: row.remarks ?? "",
    engines: row.engines ?? "",
    cabin: row.cabin ?? "",
    year: row.year ?? "",
    role: row.role ?? "",
    maxPax: row.maxPax ?? 0,
    ceilingFt: row.ceilingFt ?? 0,
    takeoffDistanceFt: row.takeoffDistanceFt ?? 0,
    landingDistanceFt: row.landingDistanceFt ?? 0,
  };
}

export function seedFleetFromRows(rows: CareerFleetRow[]): AircraftSpec[] {
  const checked = validateCareerFleet(rows);
  const list = checked.ok ? checked.rows : [];
  return list.map((row, index) => {
    if (rowHasFigures(row) || !FLEET.some((a) => a.icaoType.toUpperCase() === row.icaoType)) {
      return aircraftFromSetupTail(draftFromRow(row));
    }
    const catalog = FLEET.find((a) => a.icaoType.toUpperCase() === row.icaoType) ?? FLEET[0]!;
    const spec = cloneSpec(catalog);
    spec.registration = row.registration;
    if (row.name?.trim()) spec.name = row.name.trim();
    if (index > 0 || spec.id !== catalog.id) {
      spec.id = `ac-${row.registration.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
    }
    return spec;
  });
}

export type ManualDestination = {
  icao: string;
  include: boolean;
};

export function buildCareerDestinations(input: {
  homeIcao: string;
  installedOnly: boolean;
  scannedIcaos?: string[];
  manual?: ManualDestination[];
}): SceneryAirport[] {
  const home = normalizeIcaoCode(input.homeIcao) || SUGGESTED_HOME_ICAO;
  const wanted = new Set<string>([home]);
  for (const icao of input.scannedIcaos ?? []) {
    const code = normalizeIcaoCode(icao);
    if (code) wanted.add(code);
  }
  for (const row of input.manual ?? []) {
    const code = normalizeIcaoCode(row.icao);
    if (code && row.include) wanted.add(code);
  }
  const out: SceneryAirport[] = [];
  const seen = new Set<string>();
  for (const icao of wanted) {
    if (seen.has(icao)) continue;
    const rec = hydrateSceneryAirport(icao, []);
    if (!rec) {
      const catalog = lookupAirport(icao);
      if (catalog) {
        out.push(sceneryFromRecord(catalog));
        seen.add(icao);
        continue;
      }
      out.push({
        id: icao,
        icao,
        name: icao,
        city: icao,
        country: "—",
        runwayFt: resolveAirportRunway(icao, { notes: "Manual add — not in catalog" }).longestFt,
        notes: "Manual add — not in catalog",
      });
      seen.add(icao);
      continue;
    }
    out.push(rec);
    seen.add(icao);
  }
  return out;
}

/** Keep ferry origin / HQ on the Destinations list so Inbox + Flight Plans can file the opening job. */
export function ensureCareerSeedAirports(scenery: SceneryAirport[], seed: CareerSeed): SceneryAirport[] {
  const pair = openingJobPair(seed);
  const wanted = [pair.originIcao, pair.destIcao, seed.startIcao, seed.hqIcao]
    .map((code) => normalizeIcaoCode(code))
    .filter(Boolean);
  const out = [...scenery];
  const seen = new Set(out.map((s) => s.icao.toUpperCase()));
  for (const icao of wanted) {
    if (seen.has(icao)) continue;
    const rec = hydrateSceneryAirport(icao, []) ?? (lookupAirport(icao) ? sceneryFromRecord(lookupAirport(icao)!) : null);
    if (rec) {
      out.push(rec);
      seen.add(icao);
    }
  }
  return out;
}

export type CareerWizardDraft = {
  operatorName: string;
  companyKind: CompanyKind;
  homeIcao: string;
  extraBases?: string[];
  startingCapitalCad: number;
  /** Company ledger home currency. CAD default for NS ops. */
  homeCurrency?: CurrencyCode;
  adminName: string;
  pinSalt: string;
  pinHash: string;
  fleetRows: CareerFleetRow[];
  destinations: SceneryAirport[];
  destinationsInstalledOnly: boolean;
  communityFolderPath?: string;
  sceneryInventory?: SceneryPresence[];
  sceneryGateEnabled?: boolean;
  telephonyName?: string;
  useTelephonyCallsign?: boolean;
  flightNumberPrefix?: string;
  flightNumberStart?: number;
  flightNumberDigits?: 3 | 4;
  ferryNumberMode?: CompanyProfile["ferryNumberMode"];
  ferryStartSeq?: number;
  beginningNotes?: string;
  users?: CareerSetupUser[];
  airlineName?: string;
  airlineLogo?: string;
  airlineLogoKeptLocal?: boolean;
  airlineLogoPlate?: string;
};

export type CareerCommitResult = {
  company: CompanyProfile;
  admin: CrewMember;
  crew: CrewMember[];
  fleet: AircraftSpec[];
  account: CompanyAccount;
  scenery: SceneryAirport[];
  inbox: CharterRequest[];
  career: CareerState;
};

export function openingAccount(capitalCad: number, now = new Date(), homeCurrency?: CurrencyCode): CompanyAccount {
  const home = hydrateCurrency(homeCurrency);
  if (!(Number.isFinite(capitalCad) && capitalCad > 0)) return emptyAccount(now, home);
  return defaultAccount(now, Math.round(capitalCad), home);
}

export function inboxFromCareerSeed(
  seed: CareerSeed,
  fleet: AircraftSpec[],
  now = new Date(),
): CharterRequest {
  const tail =
    fleet.find((a) => a.registration.toUpperCase() === seed.primaryRegistration.toUpperCase()) ??
    fleet.find((a) => a.id === seed.primaryAircraftId) ??
    fleet[0];
  const pair = openingJobPair(seed);
  const origin = pair.originIcao;
  const dest = pair.destIcao;
  const originRec = lookupAirport(origin);
  const destRec = lookupAirport(dest);
  const ferry = seed.firstJob.kind === "ferry";
  const party = regionalParty({
    originIcao: origin,
    destIcao: dest,
    homeIcao: seed.hqIcao || dest,
    operatorName: seed.operatorName,
  });
  return {
    id: `inq-career-${now.getTime().toString(36)}`,
    createdAt: now.toISOString(),
    source: "demo",
    status: "new",
    clientName: party.clientName,
    clientEmail: party.clientEmail,
    clientCompany: party.clientCompany,
    originIcao: origin,
    destIcao: dest,
    originName: originRec?.name ?? origin,
    destName: destRec?.name ?? dest,
    departureIso: now.toISOString(),
    paxCount: ferry ? 0 : 2,
    cargoLb: 0,
    reason: seed.firstJob.summary,
    specialRequests: seed.notes,
    emailSubject: ferry
      ? `FERRY  ${tail?.registration ?? seed.primaryRegistration}  ${origin}–${dest}`
      : `CHARTER  ${origin}–${dest}`,
    emailBody: [
      seed.firstJob.summary,
      "",
      `Primary tail ${tail?.registration ?? seed.primaryRegistration}.`,
      `Start ${seed.startIcao} · HQ ${seed.hqIcao}.`,
      seed.rawText ? `Seed: ${seed.rawText}` : "",
    ]
      .filter(Boolean)
      .join("\n"),
    suggestedAircraftId: tail?.id ?? "",
    flightType: seed.firstJob.flightType,
    seasonalPurpose: ferry ? "ferry" : undefined,
    mxPositioning: false,
    tone: "broker",
  };
}

export function commitCareerStart(input: {
  wizard: CareerWizardDraft;
  seed: CareerSeed;
  now?: Date;
}): CareerCommitResult {
  const now = input.now ?? new Date();
  const fleetChecked = validateCareerFleet(input.wizard.fleetRows);
  const fleetRows = fleetChecked.ok ? fleetChecked.rows : [];
  const types = [...new Set(fleetRows.map((r) => r.icaoType))];
  const setup: CompanySetupInput = {
    operatorName: input.wizard.operatorName.trim() || input.seed.operatorName || SUGGESTED_COMPANY_NAME,
    homeIcao: normalizeIcaoCode(input.seed.hqIcao || input.wizard.homeIcao) || SUGGESTED_HOME_ICAO || "",
    extraBases: input.wizard.extraBases,
    operatedIcaoTypes: types,
    adminName: input.wizard.adminName,
    pinSalt: input.wizard.pinSalt,
    pinHash: input.wizard.pinHash,
    companyKind: input.wizard.companyKind,
    useTelephonyCallsign: Boolean(input.wizard.useTelephonyCallsign),
    telephonyName: input.wizard.telephonyName ?? "",
    flightNumberPrefix: input.wizard.flightNumberPrefix,
    flightNumberStart: input.wizard.flightNumberStart,
    flightNumberDigits: input.wizard.flightNumberDigits,
    ferryNumberMode: input.wizard.ferryNumberMode,
    ferryStartSeq: input.wizard.ferryStartSeq,
    now,
  };
  const built = completeCompanySetup(setup);
  const fleet = seedFleetFromRows(fleetRows).map((spec) => {
    const primary =
      spec.registration.toUpperCase() === input.seed.primaryRegistration.toUpperCase() || spec.id === input.seed.primaryAircraftId;
    return {
      ...spec,
      lastAirportIcao: primary
        ? input.seed.startIcao
        : built.company.homeIcao || spec.lastAirportIcao,
      baseIcao: built.company.homeIcao || spec.baseIcao,
    };
  });
  const scenery = ensureCareerSeedAirports(
    input.wizard.destinations.length
      ? input.wizard.destinations
      : buildCareerDestinations({
          homeIcao: built.company.homeIcao,
          installedOnly: input.wizard.destinationsInstalledOnly,
        }),
    input.seed,
  );
  const admin: CrewMember = {
    ...built.admin,
    communityFolderPath: input.wizard.communityFolderPath?.trim() ?? "",
    sceneryInventory: input.wizard.sceneryInventory ?? [],
    sceneryGateEnabled: input.wizard.sceneryGateEnabled ?? input.wizard.destinationsInstalledOnly,
  };
  const crew = (input.wizard.users?.length ? input.wizard.users : []).map((user) => {
    const member = newCrewMember({
      displayName: user.displayName,
      roles: user.roles,
      isAdmin: user.isAdmin,
      ratedIcaoTypes: user.ratedIcaoTypes,
      now,
    });
    member.ratedIcaoTypes = user.ratedIcaoTypes.map((type) => type.trim().toUpperCase()).filter(Boolean);
    member.pinSalt = user.pinSalt;
    member.pinHash = user.pinHash;
    return member;
  });
  const roster = crew.length ? crew : [admin];
  const lead = roster.find((member) => member.isAdmin) ?? roster[0] ?? admin;
  const capital = input.wizard.startingCapitalCad || input.seed.startingCapitalCad;
  const homeCurrency = hydrateCurrency(input.wizard.homeCurrency);
  const catalogs = hydrateMoneyCatalogs(undefined, { icaos: icaosFromScenery(scenery), currency: homeCurrency });
  const pair = openingJobPair(input.seed);
  const inbox =
    pair.originIcao && pair.destIcao && input.seed.primaryRegistration
      ? [inboxFromCareerSeed({ ...input.seed, operatorName: built.company.operatorName }, fleet, now)]
      : [];
  return {
    company: built.company,
    admin: lead,
    crew: roster,
    fleet,
    account: openingAccount(capital, now, homeCurrency),
    scenery,
    inbox,
    career: {
      playMode: "career",
      scenarioConfirmed: true,
      destinationsInstalledOnly: input.wizard.destinationsInstalledOnly,
      catalogStubs: catalogsAsStubs(catalogs),
      beginningNotes: effectiveBeginningScenarioText(input.wizard.beginningNotes || input.seed.rawText),
      airlineName: sanitizeAirlineName(input.wizard.airlineName),
      airlineLogo: sanitizeAirlineLogo(input.wizard.airlineLogo),
      airlineLogoKeptLocal:
        sanitizeAirlineLogo(input.wizard.airlineLogo).startsWith("data:") && input.wizard.airlineLogoKeptLocal === true,
      airlineLogoPlate: sanitizeAirlineLogoPlate(input.wizard.airlineLogoPlate),
    },
  };
}

export function commitCasualStart(input: {
  adminName: string;
  pinSalt: string;
  pinHash: string;
  operatorName?: string;
  homeIcao?: string;
  homeCurrency?: CurrencyCode;
  flightNumberPrefix?: string;
  flightNumberStart?: number;
  flightNumberDigits?: 3 | 4;
  ferryNumberMode?: CompanyProfile["ferryNumberMode"];
  ferryStartSeq?: number;
  airlineName?: string;
  airlineLogo?: string;
  airlineLogoKeptLocal?: boolean;
  airlineLogoPlate?: string;
  now?: Date;
}): CareerCommitResult {
  const now = input.now ?? new Date();
  const built = completeCompanySetup({
    operatorName: input.operatorName?.trim() || SUGGESTED_COMPANY_NAME,
    homeIcao: input.homeIcao || SUGGESTED_HOME_ICAO,
    operatedIcaoTypes: [],
    fleet: [],
    adminName: input.adminName,
    pinSalt: input.pinSalt,
    pinHash: input.pinHash,
    flightNumberPrefix: input.flightNumberPrefix,
    flightNumberStart: input.flightNumberStart,
    flightNumberDigits: input.flightNumberDigits,
    ferryNumberMode: input.ferryNumberMode,
    ferryStartSeq: input.ferryStartSeq,
    now,
  });
  return {
    company: built.company,
    admin: built.admin,
    crew: built.crew,
    fleet: built.fleet,
    account: emptyAccount(now, hydrateCurrency(input.homeCurrency)),
    scenery: [],
    inbox: [],
    career: {
      playMode: "casual",
      scenarioConfirmed: true,
      destinationsInstalledOnly: false,
      catalogStubs: catalogsAsStubs(hydrateMoneyCatalogs(undefined, { currency: hydrateCurrency(input.homeCurrency) })),
      beginningNotes: "",
      airlineName: sanitizeAirlineName(input.airlineName),
      airlineLogo: sanitizeAirlineLogo(input.airlineLogo),
      airlineLogoKeptLocal:
        sanitizeAirlineLogo(input.airlineLogo).startsWith("data:") && input.airlineLogoKeptLocal === true,
      airlineLogoPlate: sanitizeAirlineLogoPlate(input.airlineLogoPlate),
    },
  };
}

export { hydrateCompany };
